import imageCompression from 'browser-image-compression';
import { getImageUrl } from './imageUrl';
import secureApi from './secureApi';

interface UploadOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  onProgress?: (progress: number) => void;
}

interface UploadResult {
  success: boolean;
  s3Key?: string;
  imageUrl?: string;
  error?: string;
}

/**
 * Compress and upload an image to S3 using presigned URL for shop products
 * @param file - The image file to upload
 * @param productName - The product name for generating context
 * @param color - The product color variant
 * @param options - Upload options including compression settings
 * @returns Promise with upload result containing S3 key and image URL
 */
export const uploadProductImage = async (
  file: File,
  productName: string = 'product',
  color: string = 'default',
  options: UploadOptions = {}
): Promise<UploadResult> => {
  const {
    maxSizeMB = 0.5,
    maxWidthOrHeight = 1600,
    onProgress
  } = options;

  try {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      return {
        success: false,
        error: 'Please select a valid image file'
      };
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      return {
        success: false,
        error: 'Image size must be less than 5MB'
      };
    }

    // Compress the image
    let processedFile = file;
    if (file.size > 100 * 1024) { // Only compress files larger than 100KB
      const compressionOptions = {
        maxSizeMB,
        maxWidthOrHeight,
        useWebWorker: true,
        onProgress: (progress: number) => {
          if (onProgress) {
            onProgress(progress * 0.5); // First 50% for compression
          }
        }
      };

      processedFile = await imageCompression(file, compressionOptions);
    }

    // Generate file name and context
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    const ext = processedFile.type.split('/')[1] || 'jpg';
    
    // Sanitize product name for filename
    const sanitizedProductName = productName
      .replace(/[^a-zA-Z0-9]/g, '_')
      .toLowerCase()
      .substring(0, 20);
    
    const sanitizedColor = color
      .replace(/[^a-zA-Z0-9]/g, '_')
      .toLowerCase();
    
    const fileName = `product_${sanitizedProductName}_${sanitizedColor}_${Date.now()}.${ext}`;
    const context = `shop-module/products/${dd}-${mm}-${yyyy}`;

    // Request presigned URL using secureApi (includes auth headers automatically)
    const presignRes = await secureApi.post(
      '/api/v1/general/presigned-url',
      {
        fileName,
        contentType: processedFile.type,
        context,
      }
    );

    // Handle API response structure: may be wrapped in { statusCode, data: {...} } or direct
    const responseData = (presignRes as any)?.data || presignRes;
    const presignedUrl = responseData?.presignedUrl;
    const s3Key = responseData?.key;
    
    if (!presignedUrl || !s3Key) {
      return {
        success: false,
        error: 'Failed to get upload URL from server'
      };
    }

    // Update progress to 75%
    if (onProgress) {
      onProgress(75);
    }

    // Upload to S3
    const uploadRes = await fetch(presignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': processedFile.type },
      body: processedFile,
    });
    
    if (!uploadRes.ok) {
      const errorText = await uploadRes.text().catch(() => 'Unknown error');
      return {
        success: false,
        error: `Upload failed: ${uploadRes.status} ${uploadRes.statusText} - ${errorText}`
      };
    }

    // Update progress to 100%
    if (onProgress) {
      onProgress(100);
    }

    // Generate the full image URL
    const imageUrl = getImageUrl(s3Key);

    return {
      success: true,
      s3Key,
      imageUrl
    };

  } catch (error: any) {
    console.error('Product image upload error:', error);
    
    let errorMessage = 'Upload failed. Please try again.';
    
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      errorMessage = 'Upload timed out. Please check your connection and try again.';
    } else if (error.response?.status === 413) {
      errorMessage = 'Image file is too large. Please choose a smaller image.';
    } else if (error.response?.status >= 500) {
      errorMessage = 'Server error. Please try again later.';
    } else if (error.message?.includes('Failed to fetch')) {
      errorMessage = 'Network error. Please check your connection.';
    }
    
    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Upload multiple images for different color variants
 * @param files - Array of files to upload
 * @param productName - Product name for context
 * @param color - Color variant
 * @param onProgress - Progress callback for overall progress
 * @returns Promise with array of upload results
 */
export const uploadMultipleProductImages = async (
  files: File[],
  productName: string,
  color: string,
  onProgress?: (progress: number) => void
): Promise<UploadResult[]> => {
  if (files.length === 0) {
    return [];
  }

  const results: UploadResult[] = [];
  const totalFiles = files.length;

  for (let i = 0; i < totalFiles; i++) {
    const file = files[i];
    
    const fileProgress = (progress: number) => {
      const overallProgress = ((i / totalFiles) * 100) + (progress / totalFiles);
      if (onProgress) {
        onProgress(Math.min(overallProgress, 100));
      }
    };

    const result = await uploadProductImage(file, productName, color, {
      onProgress: fileProgress
    });

    results.push(result);

    // If upload failed, still continue with other files
    if (!result.success) {
      console.warn(`Failed to upload image ${i + 1}:`, result.error);
    }
  }

  return results;
};